import { checkBackup, describeBackup, exportBackup, restoreBackup } from '@quickreport/sync';
import { Button, Field, Input, Select } from '@quickreport/ui';
import { useEffect, useState } from 'react';
import { type BackupEntry, desktop, type UpdateStatus } from '../../lib/desktop';
import {
  calibrationHtml,
  loadProfile,
  type PrintProfile,
  saveProfile,
  usePrinters,
} from '../../lib/desktop-print';
import { useWorkspace } from '../../lib/workspace';

/** Settings that only exist in the desktop app: printers, backups and updates. */
export function DesktopPanel() {
  const d = desktop();
  if (!d) return null;
  return (
    <section className="grid gap-6">
      <h2 className="text-base font-semibold">This computer (desktop app)</h2>
      <PrintSettings />
      <Backups />
      <Updates />
    </section>
  );
}

function PrintSettings() {
  const d = desktop();
  const printers = usePrinters();
  const [p, setP] = useState<PrintProfile>(loadProfile());
  const set = (patch: Partial<PrintProfile>) => {
    const next = { ...p, ...patch };
    setP(next);
    saveProfile(next);
  };
  const num = (v: string) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const options = (
    <>
      <option value="">System default</option>
      {printers.map((x) => (
        <option key={x.name} value={x.name}>
          {x.displayName}
          {x.isDefault ? ' (default)' : ''}
        </option>
      ))}
    </>
  );
  return (
    <div className="grid gap-3 rounded-lg border border-line bg-paper p-4">
      <h3 className="text-[13px] font-semibold">Printing</h3>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Report printer">
          <Select value={p.printer} onChange={(e) => set({ printer: e.target.value })}>
            {options}
          </Select>
        </Field>
        <Field label="Counter slip (receipt) printer">
          <Select value={p.slipPrinter} onChange={(e) => set({ slipPrinter: e.target.value })}>
            {options}
          </Select>
        </Field>
        <Field label="Report paper">
          <Select value={p.paper} onChange={(e) => set({ paper: e.target.value as 'A4' | 'A5' })}>
            <option value="A4">A4</option>
            <option value="A5">A5</option>
          </Select>
        </Field>
        <Field label="Copies">
          <Input
            type="number"
            min={1}
            max={20}
            value={p.copies}
            onChange={(e) => set({ copies: Math.max(1, num(e.target.value)) })}
          />
        </Field>
        <Field label="Letterhead shift right (mm)">
          <Input
            type="number"
            step="0.5"
            value={p.offsetXmm}
            onChange={(e) => set({ offsetXmm: num(e.target.value) })}
          />
        </Field>
        <Field label="Letterhead shift down (mm)">
          <Input
            type="number"
            step="0.5"
            value={p.offsetYmm}
            onChange={(e) => set({ offsetYmm: num(e.target.value) })}
          />
        </Field>
      </div>
      <label className="flex items-center gap-2 text-[13px]">
        <input
          type="checkbox"
          className="accent-brand"
          checked={p.silent}
          onChange={(e) => set({ silent: e.target.checked })}
        />
        Print straight to the printer without asking each time
      </label>
      <div>
        <Button
          size="sm"
          onClick={() =>
            void d?.print.html(calibrationHtml(p), {
              ...(p.printer ? { deviceName: p.printer } : {}),
              silent: false,
              paper: p.paper,
            })
          }
        >
          Print a test page
        </Button>
        <p className="mt-2 text-[12px] text-slate">
          The test page has a 10 mm grid. If it lands off the edge of your letterhead, shift it here
          until it lines up.
        </p>
      </div>
    </div>
  );
}

function Backups() {
  const d = desktop();
  const ws = useWorkspace();
  const [list, setList] = useState<BackupEntry[]>([]);
  const [pass, setPass] = useState('');
  const [note, setNote] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () => void d?.backup.list().then(setList);
  // biome-ignore lint/correctness/useExhaustiveDependencies: load once when the panel opens
  useEffect(refresh, []);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setNote(null);
    try {
      setNote({ tone: 'ok', text: await fn() });
    } catch (err) {
      setNote({ tone: 'error', text: err instanceof Error ? err.message : 'That did not work' });
    } finally {
      setBusy(false);
      refresh();
    }
  }

  const backup = () =>
    run(async () => {
      if (pass.length < 8)
        throw new Error(
          'Choose a passphrase of at least 8 characters. You will need it to restore.',
        );
      const file = await exportBackup(ws.db, { orgId: ws.orgId, orgName: ws.auth.orgName });
      const r = await d?.backup.save({
        json: JSON.stringify(file),
        passphrase: pass,
        orgName: ws.auth.orgName,
      });
      return `Saved (${describeBackup(file)}) to ${r?.path}`;
    });

  const restore = (path: string | null) =>
    run(async () => {
      if (!path) return 'No file chosen.';
      if (pass.length < 8) throw new Error('Enter the passphrase the backup was made with.');
      const opened = await d?.backup.open({ path, passphrase: pass });
      const parsed = checkBackup(JSON.parse(opened?.json ?? 'null'));
      if (parsed.orgId !== ws.orgId)
        throw new Error(`That backup is from another lab (${parsed.orgName}).`);
      if (
        !window.confirm(
          `Replace everything on this computer with this backup (${describeBackup(parsed)})? Changes made since the backup will be lost here.`,
        )
      )
        return 'Restore cancelled.';
      ws.engine.stop();
      await restoreBackup(ws.db, parsed);
      window.location.reload();
      return 'Restored. Reloading…';
    });

  return (
    <div className="grid gap-3 rounded-lg border border-line bg-paper p-4">
      <h3 className="text-[13px] font-semibold">Backups</h3>
      <p className="text-[12px] text-slate">
        A backup is an encrypted file with this computer's data, including changes that have not
        reached the server yet. Keep the passphrase safe: nobody, including us, can open a backup
        without it.
      </p>
      <Field label="Passphrase">
        <Input
          type="password"
          autoComplete="new-password"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          className="max-w-xs"
        />
      </Field>
      <div className="flex flex-wrap gap-2">
        <Button tone="primary" size="sm" disabled={busy} onClick={() => void backup()}>
          Back up now
        </Button>
        <Button
          size="sm"
          disabled={busy}
          onClick={async () => void restore((await d?.backup.chooseFile()) ?? null)}
        >
          Restore from a file…
        </Button>
      </div>
      {note ? (
        <p
          role={note.tone === 'error' ? 'alert' : 'status'}
          className={`text-[13px] ${note.tone === 'ok' ? 'text-ok' : 'text-flag'}`}
        >
          {note.text}
        </p>
      ) : null}
      {list.length ? (
        <ul className="divide-y divide-line text-[13px]">
          {list.slice(0, 8).map((b) => (
            <li key={b.path} className="flex items-center gap-3 py-1.5">
              <span className="min-w-0 flex-1 truncate">
                {new Date(b.modified).toLocaleString('en-IN', {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
                <span className="ml-2 text-slate">{Math.round(b.size / 1024)} KB</span>
              </span>
              <button
                type="button"
                className="text-brand hover:underline"
                onClick={() => void d?.backup.reveal(b.path)}
              >
                Show in folder
              </button>
              <button
                type="button"
                className="text-brand hover:underline"
                disabled={busy}
                onClick={() => void restore(b.path)}
              >
                Restore
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Updates() {
  const d = desktop();
  const [s, setS] = useState<UpdateStatus>({ state: 'idle' });
  useEffect(() => {
    if (!d) return;
    void d.updates.status().then(setS);
    return d.updates.onStatus(setS);
  }, [d]);
  const text =
    s.state === 'disabled'
      ? 'Updates are not set up in this build.'
      : s.state === 'checking'
        ? 'Checking for updates…'
        : s.state === 'available'
          ? `Version ${s.version} is downloading…`
          : s.state === 'downloading'
            ? `Downloading the update… ${s.percent}%`
            : s.state === 'ready'
              ? `Version ${s.version} is ready.`
              : s.state === 'error'
                ? `Could not check: ${s.message}`
                : 'LabView is up to date.';
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-paper p-4 text-[13px]">
      <span className="flex-1">
        Version {d?.version}. {text}
      </span>
      {s.state === 'ready' ? (
        <Button size="sm" tone="primary" onClick={() => void d?.updates.install()}>
          Restart to update
        </Button>
      ) : s.state !== 'disabled' ? (
        <Button size="sm" onClick={() => void d?.updates.check().then(setS)}>
          Check now
        </Button>
      ) : null}
    </div>
  );
}
